import { get } from 'lodash'

/**
 * Will format ValidationError messages from NestJS API.
 * The expected structure of an error is:
 * ```
 * [{
 *   property: 'object property',
 *   constraints = { 'object constraint': 'The error message' }
 * }, ...]
 */
const formatFromNest = msg => {
  let result = ''

  for (const [key, value] of Object.entries(msg.constraints)) {
    result = result.concat(`<strong>${msg.property} | ${key}</strong>: ${value}<br />`)
  }

  return result
}

/**
 * Will format errors from Rails JSON API.
 * The expected structure of an error is:
 * ```
 * {
 *   property1: ['error1', 'error2', ...],
 *   property2: ['error1', 'error2', ...],
 *   ...
 * }
 *
 * TODO: It is not exactly following the JSON API spec: https://jsonapi.org/format/#error-objects
 */
const formatFromRailsJsonApi = (attribute, constraintsOrNestedAttributes) => {
  let result = ''

  if (attribute !== 'base') {
    result = result.concat(`<strong>${attribute}</strong>:<br />`)
  }

  if (Array.isArray(constraintsOrNestedAttributes)) {
    constraintsOrNestedAttributes.forEach(
      constraint => (result = result.concat(constraint + '<br />'))
    )
    return result
  } else if (typeof constraintsOrNestedAttributes !== 'object' || !constraintsOrNestedAttributes) {
    // A string leaf (e.g. `{ title, details }`) would otherwise recurse over its own characters.
    return result.concat(String(constraintsOrNestedAttributes) + '<br />')
  } else {
    // In order to support nested attributes:
    return Object.keys(constraintsOrNestedAttributes)
      .map(errorKey =>
        formatFromRailsJsonApi(
          [attribute, errorKey].join(' > '),
          constraintsOrNestedAttributes[errorKey]
        )
      )
      .join('<br />')
  }
}

/**
 * Will format errors as JSON API spec format: https://jsonapi.org/format/#error-objects
 */
const formatFromJsonApi = error => {
  let result = ''

  result = result.concat(`<strong>${error.title}</strong>`)

  if (error.detail) {
    result = result.concat(`<br /><span>${error.detail}</span>`)
  }

  return result
}

/**
 * AL-508: a payment declined by the gateway/bank carries a canonical decline
 * `code` (see the backend `PaymentDeclineCode` enum). Returns the localized,
 * actionable message for that code, or `null` when the error carries no
 * (recognized) decline code so callers can fall back to generic handling.
 */
const paymentDeclineMessage = (app, error) => {
  const declineCode = get(error, 'response.data.code')

  if (!declineCode) {
    return null
  }

  const declineKey = `domain.payment.declineReason.${declineCode}`

  return app.i18n.te(declineKey) ? app.i18n.t(declineKey) : null
}

/**
 * AL-508: Accept.js validates the card client-side before tokenizing, so its
 * rejections never reach our backend. Map them to our canonical decline codes
 * and wrap the result in an axios-shaped error, so the same pipeline
 * ($paymentDeclineMessage / $formatServerError) handles it.
 *
 * Code map per the official Accept.js error appendix:
 *   https://developer.authorize.net/api/reference/features/acceptjs.html#Appendix_Error_Codes
 *
 *   E_WC_05                       — Card fails Luhn validation       -> invalidCardNumber
 *   E_WC_06 / E_WC_07 / E_WC_08   — Expiration month/year/past date  -> expiredCard
 *   E_WC_15                       — CVV not 3-4 digits               -> invalidCvv
 *
 * Anything else (CDN/HTTPS/encryption/auth/etc.) is a config or input-shape
 * problem rather than a card decline, so it buckets to processingError. The
 * regex-on-text pass after the code lookup is a safety net for any future
 * SDK additions whose text matches a known pattern.
 */
const ACCEPT_JS_DECLINE_CODE_MAP = {
  E_WC_05: 'invalidCardNumber',
  E_WC_06: 'expiredCard',
  E_WC_07: 'expiredCard',
  E_WC_08: 'expiredCard',
  E_WC_15: 'invalidCvv'
}

const matchAcceptJsErrorByText = text => {
  const lowered = (text || '').toLowerCase()
  if (/expir/.test(lowered)) return 'expiredCard'
  if (/cvv|card code|security code|card verification/.test(lowered)) return 'invalidCvv'
  if (/card number|credit card/.test(lowered)) return 'invalidCardNumber'
  return null
}

const acceptJsErrorToAxiosError = response => {
  const messages = get(response, 'messages.message') || []
  const primaryCode = get(messages, '[0].code')
  const text = messages
    .map(m => m && m.text)
    .filter(Boolean)
    .join('; ')

  // Primary (first) code drives the canonical reason; the joined-text regex is
  // a safety net for SDK additions whose code we don't know yet. The two are
  // intentionally not consistent when they disagree — codes are stable, text
  // wording is not.
  const code =
    ACCEPT_JS_DECLINE_CODE_MAP[primaryCode] || matchAcceptJsErrorByText(text) || 'processingError'

  const err = new Error(text || 'Accept.js validation error')
  err.response = { data: { code, message: text } }
  return err
}

export default ({ app }, inject) => {
  inject('paymentDeclineMessage', error => paymentDeclineMessage(app, error))
  inject('acceptJsErrorToAxiosError', acceptJsErrorToAxiosError)

  inject('formatServerError', function(error, key = 'message') {
    // A declined payment gets a localized, actionable message; anything else
    // falls through to the generic formatting below.
    const declineMessage = paymentDeclineMessage(app, error)
    if (declineMessage) {
      return declineMessage
    }

    // Default message for all unauthorized requests
    if (get(error, 'response.status') === 401) return app.i18n.t('genericErrors.unauthorized')

    const errors = get(error, `response.data.${key}`)

    if (
      Array.isArray(errors) &&
      errors.every(e => e && typeof e === 'object' && 'constraints' in e && 'property' in e)
    ) {
      return errors.map(e => formatFromNest(e)).join('<br /><br />')
    } else if (
      Array.isArray(errors) &&
      errors.every(e => e && typeof e === 'object' && 'title' in e)
    ) {
      return errors.map(e => formatFromJsonApi(e)).join('<br /><br />')
    } else if (errors instanceof Object && !Array.isArray(errors)) {
      return Object.keys(errors)
        .map(errorKey => formatFromRailsJsonApi(errorKey, errors[errorKey]))
        .join('<br />')
    } else if (!errors) {
      /**
       * NOTE: Will help debugging when the coming error is not being produced by axios, but is
       * being unproperly handled with `formatServerError`.
       */
      console.error(error.message)

      return app.i18n.t('genericErrors.internalServer')
    } else {
      return errors
    }
  })
}
